import {
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Square2StackIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import {
  AccountType,
  ContractorFieldProps,
  ContractorOrigin,
  contractorOriginOptions,
  ensureContractorSections,
  isContractorOriginHasStatus,
  userHasAccountType,
} from 'config'
import type { InputType } from 'config/input.type.constants'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  getAdminConfig,
  getContractorApplication,
  sendContractorApplication,
  updateContractorApplication,
} from 'services/apis'
import { Button, FormTableHeader } from 'stories/components'
import { confirm, fillObject, openAuditLog, renderTableHistoryValue, validateEmail } from 'utils'
import { copyClipboard } from 'utils/copyClipboard'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { getContractorFieldValues, getVisibleContractorInputs } from './contractorInputVisibility'
import { ContractorPaymentSection } from './ContractorPaymentSection'
import {
  AcknowledgementSign,
  CapabilityStatement,
  ConstructionTrackRecords,
  ContractorConsentForm,
  ContractorDocumentsPage,
  ContractorReferences,
  GeneralInformation,
  ReferenceKey,
  Welcome,
} from './pages'
import { validateInputRecord } from './validateInputRecord'

export * from './ContractorDocuments'
export * from './type'

export default function ContractorApplication(props: any) {
  const { no } = useParams() as any
  const loanNumber = setLoanNumber()
  const [menus, setMenus] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<Record<string, boolean>>(
    fillObject(Object.values(ContractorOrigin).filter(isContractorOriginHasStatus), false),
  )
  const [activeMenu, setActiveMenu] = useState('welcome')
  const [partyContractorInfo, setPartyContractorInfo] = useState<any>({})
  const [contractorGeneralInfo, setContractorGeneralInfo] = useState<Record<string, InputType>>({})
  const [capabilityInfo, setCapabilityInfo] = useState<Record<string, InputType>>({})
  const [references, setReferences] = useState<any>({
    [ContractorOrigin.SubcontractorReferences]: {
      inputs: {},
      data: [],
    },
    [ContractorOrigin.SupplierReferences]: {
      inputs: {},
      data: [],
    },
    [ContractorOrigin.ClientReferences]: {
      inputs: {},
      data: [],
    },
    [ContractorOrigin.ConstructionTrackRecords]: {
      inputs: {},
      data: [],
    },
  })
  const [lockContractor, setLockContractor] = useState(false)
  const [approveContractor, setApproveContractor] = useState(false)
  const [agreeSign, setAgreeSign] = useState(false)
  const [agreeConsent, setAgreeConsent] = useState(false)
  const [signData, setSignData] = useState<any>({})
  const [consentSignData, setConsentSignData] = useState<any>({})
  const [signContent, setSignContent] = useState([])
  const [consentContent, setConsentContent] = useState('')
  const [action, setAction] = useState('')
  const [requiredTracks, setRequiredTracks] = useState<number>(5)
  const { hasPermission } = usePermissions()
  const isAdmin = hasPermission(['AdminTools', 'AdminTools.AppraiserSettlementContractor'])
  const canOverride = hasPermission('Loan.ContractorApplication.Override')
  const [fieldProperties, setFieldProperties] = useState<ContractorFieldProps>()

  const { auth, rulesConfig } = useSelector((state: any) => {
    return {
      auth: state.auth,
      rulesConfig: state.globalConfigReducer[GlobalConfigType.RulesConfig],
    }
  })

  const canEdit = useMemo(() => {
    let rlt = true
    if (signData.fileKey) rlt = false
    if (lockContractor) rlt = false
    const matched = auth?.role?.role?.permissions?.find(
      (permissionName: string) => permissionName === 'Loan.Detail.Override',
    )
    if (matched) rlt = true
    if (userHasAccountType(auth.profile, [AccountType.VENDOR_APPROVAL, AccountType.FEASIBILITY_ANALYST])) rlt = true
    return rlt
  }, [auth, signData, lockContractor])

  const fullPermission = hasPermission('Loan.ContractorApplication.Status')

  const contractorURL = props.contractor

  const checkReferenceStatus = (data: any, temp: any) => {
    temp[ContractorOrigin.SubcontractorReferences] = data.subcontractor.data.length >= 3
    temp[ContractorOrigin.SupplierReferences] = data.supplier.data.length >= 3
    temp[ContractorOrigin.ClientReferences] = data.client.data.length >= 3
    temp[ContractorOrigin.ConstructionTrackRecords] = data.track.data.length >= requiredTracks
    return temp
  }

  const init = async () => {
    setLoading(true)
    const [res, fieldProperties] = await Promise.all([
      getContractorApplication(no),
      getAdminConfig('contractorFieldProperties') as Promise<ContractorFieldProps>,
    ])

    setFieldProperties(ensureContractorSections(fieldProperties))

    setRequiredTracks(Number(rulesConfig.constructionTrackValidation))
    if (res.success) {
      let temp = cloneDeep(status)
      const { subcontractor, supplier, client, track, agreeSign, signData, customInformation } = res.contractor

      const contractorGeneralInfo: Record<string, InputType> = {}
      Object.keys(fieldProperties[ContractorOrigin.GeneralInformation].inputs).forEach((key) => {
        contractorGeneralInfo[key] = fieldProperties[ContractorOrigin.GeneralInformation].inputs[key] as InputType
        contractorGeneralInfo[key].value =
          res.contractor[key] ??
          customInformation[ContractorOrigin.GeneralInformation]?.[key] ??
          (contractorGeneralInfo[key] as any).defaultValue
      })

      const capabilityInfo: Record<string, InputType> = {}
      Object.keys(fieldProperties[ContractorOrigin.CapabilityStatement].inputs).forEach((key) => {
        capabilityInfo[key] = fieldProperties[ContractorOrigin.CapabilityStatement].inputs[key] as InputType
        capabilityInfo[key].value =
          res.contractor[key] ??
          customInformation[ContractorOrigin.CapabilityStatement]?.[key] ??
          (capabilityInfo[key] as any).defaultValue
      })

      const generalFieldValues = getContractorFieldValues(contractorGeneralInfo, capabilityInfo)
      const _contractorGeneralInfo = validateInputRecord(
        getVisibleContractorInputs(contractorGeneralInfo, generalFieldValues),
      )
      setContractorGeneralInfo({ ...contractorGeneralInfo, ..._contractorGeneralInfo.data })
      temp[ContractorOrigin.GeneralInformation] = _contractorGeneralInfo.success

      const _capabilityInfo = validateInputRecord(capabilityInfo)
      setCapabilityInfo(_capabilityInfo.data)
      temp[ContractorOrigin.CapabilityStatement] = _capabilityInfo.success

      const references = {
        subcontractor: {
          inputs: fieldProperties[ContractorOrigin.SubcontractorReferences].inputs,
          data: subcontractor,
        },
        supplier: {
          inputs: fieldProperties[ContractorOrigin.SupplierReferences].inputs,
          data: supplier,
        },
        client: {
          inputs: fieldProperties[ContractorOrigin.ClientReferences].inputs,
          data: client,
        },
        track: {
          inputs: fieldProperties[ContractorOrigin.ConstructionTrackRecords].inputs,
          data: track,
        },
      }

      setStatus(checkReferenceStatus(references, temp))
      setReferences(references)

      setLockContractor(lockContractor)
      setApproveContractor(approveContractor)
      setAgreeSign(!!agreeSign)
      setAgreeConsent(!!res.contractor.agreeConsent)
      setSignData(signData || {})
      setConsentSignData(res.contractor.consentSignData || {})
      setSignContent(res.signContent)
      setConsentContent(res.consentContent || '')
      setPartyContractorInfo(res.partyContractorInfo)
      if (props.menu) setActiveMenu(props.menu)
    }
    setLoading(false)

    const newMenus: Record<string, string> = {} as any
    Object.keys(ContractorOrigin).forEach((_origin) => {
      const origin = (ContractorOrigin as any)[_origin] as ContractorOrigin
      newMenus[origin] = fieldProperties?.[origin]?.title ?? contractorOriginOptions[origin as ContractorOrigin]
    })
    if (contractorURL) delete newMenus[ContractorOrigin.Documents]
    setMenus(newMenus)
  }

  useEffect(() => {
    let success = true
    Object.keys(status).map((key) => {
      if (!status[key]) success = false
    })
    if (props.appStatusChange) props.appStatusChange(success)
  }, [status])

  useEffect(() => {
    setStatus(checkReferenceStatus(references, status))
  }, [references])

  useEffect(() => {
    let temp = cloneDeep(status)
    // Coerce to real booleans — `a && (b || c)` can yield null/undefined when
    // API fields are null, which hides both sidebar check and X icons while
    // Acknowledgement & Sign still treats the step as incomplete.
    temp.sign = !!(agreeSign && (signData.fileKey?.length > 0 || signData.override))
    temp[ContractorOrigin.ConsentForm] = !!(
      agreeConsent &&
      (consentSignData.fileKey?.length > 0 || consentSignData.override)
    )
    setStatus(temp)
  }, [agreeSign, signData, agreeConsent, consentSignData])

  useEffect(() => {
    setLoanNumber()
    init()
  }, [])

  const renderPreNext = useMemo(() => {
    let orderMenuKeys: any = {}
    let currentOrder = -1

    Object.keys(menus).map((key: string, index: number) => {
      orderMenuKeys[index] = key
      if (key === activeMenu) currentOrder = index
    })

    let prevKey = '',
      nextKey = ''
    try {
      prevKey = orderMenuKeys[currentOrder - 1]
    } catch {}
    try {
      nextKey = orderMenuKeys[currentOrder + 1]
    } catch {}

    const cn = `px-9 py-1 border border-shade-blue rounded cursor-pointer text-shade-blue hover:bg-shade-blue hover:text-white`
    return (
      <div className="flex flex-wrap gap-4 justify-center my-5">
        {prevKey && (
          <div className={cn} onClick={() => setActiveMenu(prevKey)}>
            <span className="">
              <ChevronLeftIcon className="w-6 h-6"></ChevronLeftIcon>
            </span>
          </div>
        )}
        {nextKey && (
          <div className={cn} onClick={() => setActiveMenu(nextKey)}>
            <span className="">
              <ChevronRightIcon className="w-6 h-6"></ChevronRightIcon>
            </span>
          </div>
        )}
      </div>
    )
  }, [activeMenu])

  const onChangeLockContractor = async (key: string, value: boolean) => {
    let json: any = {
      [key]: value,
    }
    if (!value) {
      if (signData.fileKey) {
        const result = await confirm('The contractor needs to sign the application again.<br/>Do you want to proceed?')
        if (!result) return
      }
      json.signData = {}
      json.consentSignData = {}
      json.agreeConsent = false
    }
    setLoading(true)
    await updateContractorApplication(no, json)
    setLockContractor(value)
    if (!value) {
      setSignData({})
      setConsentSignData({})
      setAgreeConsent(false)
    }
    setLoading(false)
  }

  const onChangeApproveContractor = async (key: string, value: boolean) => {
    if (value) {
      const result = await confirm(
        'Are you sure you are going to approve this contractor?<br/>You must make sure that all necessary documents are uploaded by the contractor, and then you approved it.',
      )
      if (!result) return
    }
    setLoading(true)
    await updateContractorApplication(no, { [key]: value })
    setApproveContractor(value)
    setLoading(false)
  }

  const showHistory = async (key: string, headers: Array<FormTableHeader> = []) => {
    const options = {
      table: 'Contractor',
      field: `${key}`,
      keys: {
        no,
        field: key,
      },
      renderValue:
        key === 'signData' || key === 'consentSignData'
          ? (data: any) => <span>{data.override ? 'Yes' : 'No'}</span>
          : headers.length
          ? (data: any) => renderTableHistoryValue(data, headers)
          : null,
    }
    openAuditLog(options)
  }

  const onSubmitReferences = (
    currentId: any,
    values: Record<string, any>,
    key: string,
  ): Promise<Record<string, any>> => {
    return new Promise(async (resolve) => {
      setLoading(true)
      let temp: any = cloneDeep(references)
      if (!currentId) {
        values.id = Date.now()
        temp[key].data.push(values)
      } else {
        temp[key].data.map((item: any, index: number) => {
          if (item.id == currentId) {
            temp[key].data[index] = {
              ...temp[key].data[index],
              ...values,
            }
          }
        })
      }
      await updateContractorApplication(no, { [key]: temp[key].data })
      setReferences(temp)
      setLoading(false)
      resolve(values)
    })
  }

  const onRemoveReferences = async (id: any, key: string) => {
    return new Promise(async (resolve) => {
      setLoading(true)
      let temp: any = []
      references[key].data.map((item: any) => {
        if (item.id != id) temp.push(item)
      })
      await updateContractorApplication(no, { [key]: temp })
      let refTemp = cloneDeep(references)
      refTemp[key].data = temp
      setReferences(refTemp)
      setLoading(false)

      resolve(true)
    })
  }

  const sendApplication = async (copyURL: boolean) => {
    if (!validateEmail(contractorGeneralInfo.entityEmail.value)) {
      return toast(`Please input valid contractor's entity email!`, { type: 'error' })
    }
    if (!copyURL && signData.fileKey) {
      return toast('Application already signed!', { type: 'error' })
    }
    setLoading(true)
    setAction(copyURL ? 'copyAppURL' : 'sendApplication')
    const res = await sendContractorApplication(no, { copyURL })
    if (copyURL && res.success) {
      copyClipboard(res.link)
    }
    setLoading(false)
    setAction('')
  }

  const renderContent = () => {
    if (!fieldProperties) return null

    switch (activeMenu) {
      case ContractorOrigin.Welcome:
        return <Welcome props={fieldProperties[ContractorOrigin.Welcome]} />
      case ContractorOrigin.GeneralInformation:
        return (
          <>
          <GeneralInformation
            inputs={contractorGeneralInfo}
            capabilityInputs={capabilityInfo}
            sections={fieldProperties[ContractorOrigin.GeneralInformation]?.sections}
            setInputs={setContractorGeneralInfo}
            partyContractorInfo={partyContractorInfo}
            onStatusChange={(ok) => setStatus((s) => ({ ...s, [ContractorOrigin.GeneralInformation]: ok }))}
            canEdit={canEdit}
            contractorURL={contractorURL}
            showHistory={showHistory}
            setLoading={setLoading}
          />
          {/* Internal only: never shown on the contractor-facing application link. */}
          {!contractorURL && <ContractorPaymentSection contractorNo={Number(no)} />}
          </>
        )
      case ContractorOrigin.CapabilityStatement:
        return (
          <CapabilityStatement
            inputs={capabilityInfo}
            sections={fieldProperties[ContractorOrigin.CapabilityStatement]?.sections}
            setInputs={setCapabilityInfo}
            onStatusChange={(ok) => setStatus((s) => ({ ...s, [ContractorOrigin.CapabilityStatement]: ok }))}
            canEdit={canEdit}
            showHistory={showHistory}
            setLoading={setLoading}
          />
        )
      case ContractorOrigin.SubcontractorReferences:
      case ContractorOrigin.SupplierReferences:
      case ContractorOrigin.ClientReferences:
        return (
          <ContractorReferences
            referenceKey={activeMenu as ReferenceKey}
            status={status}
            canEdit={canEdit}
            inputs={references[activeMenu as ReferenceKey].inputs}
            defaultData={references[activeMenu as ReferenceKey].data}
            onSubmitReferences={onSubmitReferences}
            onRemoveReferences={onRemoveReferences}
            showHistory={showHistory}
          />
        )
      case ContractorOrigin.ConstructionTrackRecords:
        return (
          <ConstructionTrackRecords
            requiredTracks={requiredTracks}
            trackStatusOk={status[ContractorOrigin.ConstructionTrackRecords]}
            canEdit={canEdit}
            inputs={references[ContractorOrigin.ConstructionTrackRecords].inputs}
            defaultData={references[ContractorOrigin.ConstructionTrackRecords].data}
            onSubmitTrack={onSubmitReferences}
            onRemoveTrack={onRemoveReferences}
            showHistory={showHistory}
          />
        )
      case ContractorOrigin.ConsentForm:
        return (
          <ContractorConsentForm
            consentContent={consentContent}
            agreeConsent={agreeConsent}
            onAgreeConsentUpdated={setAgreeConsent}
            canEdit={canEdit}
            showHistory={showHistory}
            consentSignData={consentSignData}
            onConsentSignDataChange={setConsentSignData}
            contractorGeneralInfo={contractorGeneralInfo}
            contractorURL={contractorURL}
            canOverride={!!canOverride}
            setLoading={setLoading}
            onSigned={() => setActiveMenu(ContractorOrigin.AcknowledgementSign)}
          />
        )
      case ContractorOrigin.AcknowledgementSign:
        return (
          <AcknowledgementSign
            signContent={signContent}
            agreeSign={agreeSign}
            onAgreeSignUpdated={setAgreeSign}
            canEdit={canEdit}
            showHistory={showHistory}
            signData={signData}
            onSignDataChange={setSignData}
            contractorGeneralInfo={contractorGeneralInfo}
            sendApplication={sendApplication}
            action={action}
            contractorURL={contractorURL}
            changeMenu={props.changeMenu}
            canOverride={!!canOverride}
            status={status}
            menus={menus}
            setLoading={setLoading}
          />
        )
      case ContractorOrigin.Documents:
        return <ContractorDocumentsPage setLoading={setLoading} canChangeStatus={!!fullPermission} />
      default:
        return null
    }
  }

  const totalStatusFragment = useMemo(() => {
    let rlt = null
    if (approveContractor || lockContractor) {
      rlt = (
        <div className="px-4 pt-4 flex justify-end flex-wrap gap-4 italic -mb-4 font-bold text-[13px]">
          {lockContractor && <div className="text-red-700">- Locked</div>}
          {approveContractor && <div className="text-shade-blue">- Approved</div>}
        </div>
      )
    }
    return rlt
  }, [approveContractor, lockContractor])

  const isZeroLoanNumber = loanNumber == 0
  return (
    <div className="ContractorApplication-container px-2 my-6">
      {!contractorURL && (
        <Overview
          title={`Contractor/Builder Application No.${no}`}
          backLink={
            isZeroLoanNumber
              ? isAdmin
                ? '/admin_tool/configuration?section=partyEligibility&tab=constractor'
                : '/resources'
              : `/contractor_application/${loanNumber}`
          }
          backTitle={'Return to All Contractor Applications'}
        />
      )}
      <div className="relative max-w-screen-2xl m-auto grid grid-cols-12 md:gap-6">
        <LayoutLoading show={loading} />
        <div className="sidebar-left col-span-12 md:col-span-3 shrink-0 bg-white shadow1 rounded pb-6 min-h-[200px] mb-4">
          <div className="relative">
            {totalStatusFragment}
            <ul className="sidebar-items flex flex-col p-4 pb-10">
              {Object.keys(menus).map((key: string, index) => {
                const isActive = activeMenu === key
                return (
                  <li
                    key={index}
                    onClick={() => {
                      setActiveMenu(key)
                    }}
                    className="border-b py-2 flex justify-between items-center "
                  >
                    <p
                      className={`flex-1 hover:underline cursor-pointer ${
                        isActive ? 'border px-4 py-1 bg-zinc-100' : 'py-1'
                      }`}
                    >
                      <span>
                        {index + 1}. {menus[key]}
                      </span>
                    </p>

                    <span className="ml-2">
                      {status[key] === true && (
                        <span className="text-shade-blue">
                          <CheckCircleIcon className="w-4 h-4"></CheckCircleIcon>
                        </span>
                      )}
                      {status[key] === false && (
                        <span className="text-red-800">
                          <XCircleIcon className="w-4 h-4"></XCircleIcon>
                        </span>
                      )}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
        <div className="col-span-12 md:col-span-9">
          <div className="relative h-fit content-right bg-white p-4 rounded shadow1 mb-4">
            <div className="flex flex-wrap items-center gap-4 mb-5">
              <h2 className="text-2xl font-bold">{menus[activeMenu]}</h2>
              {fullPermission && activeMenu === 'sign' && (
                <div className="flex flex-wrap gap-4">
                  <RenderInput
                    input={{
                      inputType: 'toggle',
                      title: 'Lock Contractor',
                      value: lockContractor,
                      history: true,
                    }}
                    Key={'lockContractor'}
                    onChange={onChangeLockContractor}
                    showHistory={showHistory}
                  />
                </div>
              )}
              {fullPermission && activeMenu === 'documents' && (
                <div className="flex flex-wrap gap-4">
                  <RenderInput
                    input={{
                      inputType: 'toggle',
                      title: 'Approve Contractor',
                      value: approveContractor,
                      history: true,
                    }}
                    Key={'approveContractor'}
                    onChange={onChangeApproveContractor}
                    showHistory={showHistory}
                  />
                </div>
              )}
            </div>
            {!contractorURL && ['welcome', 'general'].includes(activeMenu) && (
              <div className="flex justify-center flex-wrap gap-4 my-2">
                <Button
                  className=""
                  color="gray"
                  onClick={() => sendApplication(false)}
                  loading={action === 'sendApplication'}
                >
                  <div className="flex gap-2 items-center">
                    Send Application to Contractor
                    <ArrowTrendingUpIcon className="w-4 h-4"></ArrowTrendingUpIcon>
                  </div>
                </Button>
                <Button
                  link
                  className=""
                  color="gray"
                  onClick={() => sendApplication(true)}
                  loading={action === 'copyAppURL'}
                >
                  <div className="flex gap-1 items-center">
                    Copy Application URL
                    <Square2StackIcon className="w-4 h-4"></Square2StackIcon>
                  </div>
                </Button>
              </div>
            )}
            {renderContent()}
          </div>
          <div className="">{renderPreNext}</div>
        </div>
      </div>
    </div>
  )
}
